import useInView from '@propertypal/shared/src/hooks/useInView';
import noPhoto from '@propertypal/shared/src/resources/fallbacks/no-photo-icon.png';
import { Property } from '@propertypal/shared/src/types/property';
import { getAgentLogos, getMainImage } from '@propertypal/shared/src/utils/property';
import React, { FunctionComponent, useRef } from 'react';
import { useTheme } from 'styled-components';
import PropertyPlaceholder from './PropertyPlaceholder';
import PropertyPrice from './PropertyPrice';
import {
  Container,
  Content,
  LoadingBox,
  Grid,
  Title,
  EmptyText,
  PropertyContent,
  PropertyImage,
  PropertyNoImage,
  TextBox,
  PriceRow,
  AccountLogo,
} from './SimilarPropertiesLite.style.pn';

interface Props {
  properties?: Property[];
  onInView?: () => void;
}

const PropertyBox: FunctionComponent<{ property: Property }> = ({ property }) => {
  const [heroImage] = getMainImage(property);
  const theme = useTheme();
  const accountLogos = getAgentLogos(property);

  return (
    <PropertyContent href={property.path} prefetch={false} data-nosnippet>
      {heroImage ? (
        <PropertyImage src={heroImage} />
      ) : (
        <PropertyNoImage src={noPhoto.src} alt="No photo available" data-testid="no-photo-img" />
      )}

      <TextBox>
        <p>
          <b>{property.displayAddressLine1}</b>, {property.displayAddressLine2}
        </p>

        <PriceRow>
          <PropertyPrice prefixBreak fontSize={17} primaryColor={theme.textDark} price={property.price} />

          {!!accountLogos.length && (
            <AccountLogo
              key={accountLogos[0].url}
              style={{
                backgroundColor: accountLogos[0].backgroundColor,
                backgroundImage: `url(${accountLogos[0].url})`,
              }}
            />
          )}
        </PriceRow>
      </TextBox>
    </PropertyContent>
  );
};

const SimilarPropertiesLite: FunctionComponent<Props> = (props) => {
  const container = useRef<HTMLDivElement>(null);
  useInView(container, { threshold: 0.5, rootMargin: '0px 0px 800px' }, props.onInView);

  if (!props.properties || props.properties.length === 0) {
    return (
      <Container
        ref={container}
        data-testid={`similar-properties${props.properties?.length === 0 ? '-empty' : !props.properties && '-loading'}`}
      >
        <LoadingBox>
          <Title>Similar Properties</Title>

          {!props.properties && (
            <Grid>
              <PropertyPlaceholder hideFooter />
              <PropertyPlaceholder hideFooter />
              <PropertyPlaceholder hideFooter />
              <PropertyPlaceholder hideFooter />
            </Grid>
          )}

          {props.properties?.length === 0 && (
            <EmptyText>Sorry, we couldn&apos;t find any similar properties to this property.</EmptyText>
          )}
        </LoadingBox>
      </Container>
    );
  }

  if (props.properties && props.properties.length > 0) {
    return (
      <Container data-testid="similarProperties">
        <Content>
          <Title>Similar Properties</Title>
          <Grid>
            {props.properties.map((property) => (
              <PropertyBox key={property.id} property={property} />
            ))}
          </Grid>
        </Content>
      </Container>
    );
  }

  return null;
};

export default SimilarPropertiesLite;
